import { useEffect, useRef, useState } from 'react';
import { useAtomValue, useAtom, useSetAtom } from 'jotai';
import { selectedThemeMapAtom, isTerrainControlerOpenAtom, isDrawControlOpenAtom, selectedLayersAtom, menuAtom, isSeparateDisplayControllerAtom, mainMapAtom, selectedBaseMapAtom, isSidebarOpenAtom, bgMapSourcesAtom, menuItemsAtom } from '../../atoms';
import './ThemeMapList.css';
import { useGetFiwareSourceIds } from '../../lib/useGetSourceIds';
import type { ThemeMapType } from '../../types/lib/types';
import { useLocation } from 'react-router-dom';
import CircleButton from '../button/CircleButton';
import SquareButton from '../button/SquareButton';
import { getMenuItemFromMenus } from '../../lib/menuUtils';
import classNames from 'classnames';
import ReactGA from 'react-ga4';
import { LayerSpecification } from 'maplibre-gl';
import { anyType } from '../../types';

// TODO 2026/05/18のセンサー入れ替え後に削除すること
const TEMPORAL_ID_MAPPINGS: { oldKey: string, newKey: string }[] = [
  {oldKey: 'リアルタイム防災情報/道路通行情報/道路規制', newKey: 'リアルタイム防災情報/道路通行規制'},
  {oldKey: 'リアルタイム防災情報/道路通行情報/地下道冠水状況', newKey: 'リアルタイム防災情報/冠水状況',}
];

const ThemeMaps:React.FC<{showMoreButton: boolean}> = () => {

  const [themeMaps, setThemeMaps] = useState<ThemeMapType[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  // atoms
  const [selectedThemeMap, setSelectedThemeMap] = useAtom(selectedThemeMapAtom);    // 選択中のテーマ図
  const setIsTerrainControlerOpen = useSetAtom(isTerrainControlerOpenAtom);         // 3D表示コントローラー表示フラグ
  const setIsDrawControlOpen = useSetAtom(isDrawControlOpenAtom);                   // 作図コントローラー表示フラグ
  const setSelectedLayers = useSetAtom(selectedLayersAtom);                      // 選択中のレイヤー全体
  const menus = useAtomValue(menuAtom);
  const menuItems = useAtomValue(menuItemsAtom);
  const mainMap = useAtomValue(mainMapAtom);
  const [selectedBaseMap, setSelectedBaseMap] = useAtom(selectedBaseMapAtom);
  const isSidebarOpen = useAtomValue(isSidebarOpenAtom);

  const bgMapSources = useAtomValue(bgMapSourcesAtom);

  const fiwareSourceIds = useGetFiwareSourceIds();
  const location = useLocation();
  const isDev = location.pathname === '/dev';

  const isSeparateDisplayController = useAtomValue(isSeparateDisplayControllerAtom);

  useEffect(() => {
    fetch(`https://yaizu-city.github.io/app/テーマ図/設定.txt?${Date.now()}`)
      .then(response => response.json())
      .then(data => { setThemeMaps(data); });
  }, []);


  /* **********************
   * スタイルをセット
   * **********************/ 
  const setStyle = async (themeMap: ThemeMapType) => {

    if (!mainMap || !selectedBaseMap) { return; }
    setSelectedThemeMap(themeMap);

    const rawNewStyle = await fetch(`${themeMap.styleUrl}?${Date.now()}`);
    const newStyle = await rawNewStyle.json();

    // データレイヤーを更新
    const newDataLayers: string[] = [];
    newStyle.layers.forEach((layer: LayerSpecification) => {
      let layerId = layer.id;
      TEMPORAL_ID_MAPPINGS.forEach((mapping) => {
        if (layerId.includes(mapping.oldKey)) {
          layerId = layerId.replace(mapping.oldKey, mapping.newKey);
        }
      })
      const targetLayer = menuItems?.find((menu) => layerId.includes(menu.id));
      if(layer.layout?.visibility === 'visible' && targetLayer) {
        newDataLayers.push(targetLayer.id);
      }
    });

    setSelectedLayers(newDataLayers);

    // 背景地図レイヤーを更新
    const themeBgLayers = bgMapSources
      .filter((bgMap) => newStyle.layers.some((layer: LayerSpecification) => 
        'source' in layer && bgMap.sources.includes((layer as anyType).source) && layer.layout?.visibility === 'visible'
      ))

    setSelectedBaseMap((prev) => {
      return themeBgLayers.length > 0 ? themeBgLayers[0] : prev;
    });

  }

  /* **********************
   * ボタンクリック時処理
   * **********************/ 
  const onClickBtn = (thmMap: ThemeMapType) => {
    // 選択中のテーマ図と同じボタンをクリックした場合はリセット
    if(selectedThemeMap?.name === thmMap.name) { 
      setSelectedThemeMap(null);
      setSelectedLayers([]);  // 選択中のレイヤーをリセット
      
      return; 
    }

    //【GoogleAnalytics】テーマ図クリックトラッキング
    ReactGA.event({
      category: "button_click", // カテゴリ（任意の名前）
      action: "click",          // イベントの種類
      label: `テーマ図/${thmMap.name}`,       // ボタン名
    });
    setIsTerrainControlerOpen(false);       // 3D表示をリセット
    setIsDrawControlOpen(undefined);            // 作図をリセット
    setStyle(thmMap);
  }


  /* *************
   * フォーマットに応じてダウンロード
   * *************/
  const downloadByFormat = (content: string, filename: string, extension: string) => {
    const blob = new Blob([content], { type: extension === 'json' ? 'application/json' : 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.download = `${filename}.${extension}`;
    a.href = url;
    a.click();
  }


  /* *************
   * ダウンロード
   * *************/
  const downLoadFile = () => {
    if (!mainMap) { return; }

    const style = mainMap.getStyle();

    // FIWAREのデータは出力時に静的データになってしまうため、空の geojson を source に追加
    for (const sourceId of fiwareSourceIds) {
      style.sources[sourceId] = { type: 'geojson', data: { type: 'FeatureCollection', features: [] } };
    }

    // menuId の状態を保存
    for (const layer of style.layers) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tileId = (layer as any)['source-layer'] || (layer as any).source; // FIWARE のデータの場合は source-layer が存在しないため、source を取得
      const item = getMenuItemFromMenus(menus, tileId);
      if (item) { layer.metadata = { menuId: item.id }; }
    }

    const styleJSON: string = JSON.stringify(style, null, 2);

    // JSONでダウンロードする場合
    downloadByFormat(styleJSON, 'style', 'json');
    
    // テキストでダウンロードする場合（庁内GIS環境で json ファイルが運用できないので）
    downloadByFormat(styleJSON, 'style', 'txt');
    
  }


  return (
    <div ref={containerRef} className={classNames("theme-maps-container", {'shift-right': !isSidebarOpen})}>
      {/* /dev の時にテーマ図作成ボタンを表示 */}
      { isDev && 
        <div className="theme-maps-create-form-container">
          <SquareButton
            label='テーマ図を作成' 
            onClick={downLoadFile}
            bold
            small
            disabled={isSeparateDisplayController}
          />
        </div>
      }
      { themeMaps.map((themeMap, index) => {
          return (
            <div className='btn-wrapper' key={index} >
              <CircleButton 
                onClick={() => onClickBtn(themeMap)} 
                label={themeMap.name} 
                color={selectedThemeMap?.name === themeMap.name ? 'primary' : 'secondary'}
                bold
                small
              />
            </div>
          )
      }) }
    </div>
  );
}

export default ThemeMaps;
